import React from "react";
import { t } from "@lingui/core/macro";

/**
 * Bulle flottante (FAB) WhatsApp, affichée globalement sur tous les thèmes
 * SAUF Ummah (qui a sa propre topbar sociale).
 *
 * Source de vérité : config["social.whatsapp"], saisi par l'admin dans les
 * réseaux sociaux. La valeur peut être une URL complète (wa.me, chat.whatsapp.com…)
 * ou un simple numéro de téléphone → normalisé en lien wa.me.
 *
 * Rien n'est rendu si aucun WhatsApp n'est configuré ou en mode embed.
 *
 * Volontairement autonome (couleurs WhatsApp officielles, styles inline + <style>
 * scopé) pour être montable dans n'importe quel thème sans dépendre de ses CSS vars.
 */

const WHATSAPP_GREEN = "#25D366";

/**
 * Normalise la valeur admin en URL cliquable.
 *  - "https://wa.me/33612345678"            → inchangé
 *  - "https://chat.whatsapp.com/XXXX"       → inchangé (lien de groupe)
 *  - "+33 6 12 34 56 78" / "0033612345678"  → "https://wa.me/33612345678"
 * Retourne null si la valeur ne donne rien d'exploitable.
 */
export function buildWhatsappUrl(raw) {
    if (!raw || typeof raw !== "string") return null;
    const value = raw.trim();
    if (!value) return null;

    // Déjà une URL (http/https) → on la garde telle quelle.
    if (/^https?:\/\//i.test(value)) return value;
    // Protocole whatsapp natif
    if (/^whatsapp:/i.test(value)) return value;
    // Saisie sans protocole mais qui ressemble à un domaine WhatsApp
    if (/^(www\.)?(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com)/i.test(value)) {
        return `https://${value.replace(/^www\./i, "")}`;
    }

    // Sinon : on tente le numéro de téléphone.
    const digits = value.replace(/[^\d]/g, "").replace(/^00/, "");
    if (digits.length < 6) return null;
    return `https://wa.me/${digits}`;
}

/**
 * @param {object}  context
 * @param {boolean} aboveChatbot  true sur Travel/Travel-2, où le chatbot occupe
 *                                déjà le coin bas-droit → on empile au-dessus.
 */
export default function WhatsappFab({ context, aboveChatbot = false }) {
    const config = context?.config || {};

    // Pas de FAB en embed (widget intégré chez un tiers) ni en app native.
    if (context?.render === "embed") return null;

    const url = buildWhatsappUrl(config["social.whatsapp"]);
    if (!url) return null;

    const label = t`Nous contacter sur WhatsApp`;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: FAB_CSS }} />
            <a
                className={"kehila-wa-fab" + (aboveChatbot ? " kehila-wa-fab--above-chatbot" : "")}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
            >
                <span className="kehila-wa-fab__pulse" aria-hidden="true" />
                <svg
                    className="kehila-wa-fab__icon"
                    viewBox="0 0 24 24"
                    width="28"
                    height="28"
                    fill="currentColor"
                    aria-hidden="true"
                    focusable="false"
                >
                    {/* Glyphe WhatsApp officiel (plein) */}
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 016.988 2.898 9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488" />
                </svg>
            </a>
        </>
    );
}

const FAB_CSS = `
.kehila-wa-fab{
    position: fixed;
    bottom: calc(20px + env(safe-area-inset-bottom, 0px));
    inset-inline-end: 20px;
    z-index: 900;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${WHATSAPP_GREEN};
    color: #fff;
    box-shadow: 0 6px 20px rgba(37, 211, 102, .45), 0 2px 6px rgba(0, 0, 0, .18);
    transition: transform .18s ease, box-shadow .18s ease;
    -webkit-tap-highlight-color: transparent;
}
.kehila-wa-fab:hover,
.kehila-wa-fab:focus-visible{
    color: #fff;
    transform: scale(1.07);
    box-shadow: 0 10px 26px rgba(37, 211, 102, .55), 0 3px 8px rgba(0, 0, 0, .2);
}
.kehila-wa-fab:focus-visible{
    outline: 3px solid rgba(37, 211, 102, .45);
    outline-offset: 3px;
}
.kehila-wa-fab:active{ transform: scale(.96); }
.kehila-wa-fab__icon{ position: relative; z-index: 1; display: block; }

/* Halo pulsé discret pour attirer l'oeil sans être agressif */
.kehila-wa-fab__pulse{
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${WHATSAPP_GREEN};
    opacity: .55;
    animation: kehila-wa-pulse 2.4s cubic-bezier(.25,.6,.35,1) infinite;
}
@keyframes kehila-wa-pulse{
    0%   { transform: scale(1);   opacity: .5; }
    70%  { transform: scale(1.7); opacity: 0; }
    100% { transform: scale(1.7); opacity: 0; }
}
@media (prefers-reduced-motion: reduce){
    .kehila-wa-fab__pulse{ animation: none; opacity: 0; }
    .kehila-wa-fab{ transition: none; }
}

/* Mobile : légèrement plus compact et plus proche du bord */
@media (max-width: 560px){
    .kehila-wa-fab{
        width: 52px;
        height: 52px;
        bottom: calc(16px + env(safe-area-inset-bottom, 0px));
        inset-inline-end: 16px;
    }
    .kehila-wa-fab__icon{ width: 26px; height: 26px; }
}

/* Thème 2026-mobile : remonter au-dessus de la bottom-bar quand elle est présente */
.theme-2026--has-bottombar .kehila-wa-fab,
.theme-2026--native-floating-bar .kehila-wa-fab{
    bottom: calc(88px + var(--sai-bottom, env(safe-area-inset-bottom, 0px)));
}

/* Travel / Travel-2 : le chatbot occupe déjà right:20px/bottom:20px (.trchat__fab,
   56px, z-index 9998) et son teaser sort à right:90px. On empile donc le FAB
   WhatsApp AU-DESSUS du chatbot plutôt qu'à côté (à côté = collision directe
   avec le teaser). Valeurs alignées sur le responsive du chatbot. */
.kehila-wa-fab--above-chatbot{ bottom: calc(88px + env(safe-area-inset-bottom, 0px)); }
@media (max-width: 560px){
    .kehila-wa-fab--above-chatbot{ bottom: calc(80px + env(safe-area-inset-bottom, 0px)); }
}
`;
